import React, { useMemo } from "react";

import { useDeviceContext } from "../../../context/DeviceContext";
import usePermissions from "../../../hooks/usePermissions";
import { updateColourScheme } from "../../../utils/nativeUtils/customActions";
import { FeatureSentiment } from "../../Shared/FeatureSentiment";
import EDSThemeToggle from "../../Shared/ThemeToggle/EDSThemeToggle";
import FullScreen from "./FullScreen";
import { GlobalSearch } from "./GlobalSearch";
import { HeaderContainerV2 } from "./HeaderContainerV2";
import { useHeaderFlags } from "./useHeaderFlags";

export type HeaderContainerProps = {
  start?: React.JSX.Element | string;
  middle?: React.JSX.Element | string;
  end?: React.JSX.Element | string;
  featureSentimentName?: string;
};

const headerItemBaseClasses =
  "HeaderContainer__headerItem flex self-center flex-1 px-panel-padding content-center h-full items-center";

export const HeaderContainer = (props: HeaderContainerProps) => {
  const { variant } = useHeaderFlags();
  if (variant === "v2") return <HeaderContainerV2 {...props} />;
  return <HeaderContainerV1 {...props} />;
};

const HeaderContainerV1 = ({
  start,
  middle,
  end,
  featureSentimentName
}: HeaderContainerProps) => {
  const { colourScheme, setTheme } = useDeviceContext();
  const { roles, capabilities } = usePermissions();

  const showGlobalSearch = useMemo(() => {
    if (roles.derived.hasRoles) {
      return roles.derived.userIsInternal || roles.booking.update;
    }

    const isInternal = capabilities.derived.userIsInternal;
    const hasBookingManagement =
      capabilities.manageVenueGroupBookings.thisVenueGroup ||
      capabilities.manageVenueBookings.some;
    const hasBookingConfirmation =
      capabilities.confirmVenueGroupBookings.thisVenueGroup ||
      capabilities.confirmVenueBookings.some;

    return isInternal || hasBookingManagement || hasBookingConfirmation;
  }, [roles, capabilities]);

  const handleThemeToggle = async (isDarkMode: boolean) => {
    const themeName = isDarkMode ? "Dark" : "Light";
    setTheme(themeName);
    await updateColourScheme(themeName);
  };

  return (
    <div className="HeaderContainer flex-[1_0_auto] self-stretch flex flex-row flex-nowrap justify-center content-start items-center bg-page-background text-heading-color pt-3">
      <div className={`${headerItemBaseClasses} justify-start`}>
        {start || ""}
        {showGlobalSearch && <GlobalSearch />}
      </div>
      <div className={`${headerItemBaseClasses} justify-center`}>
        {middle || ""}
      </div>
      <div
        className={`${headerItemBaseClasses} justify-end overflow-hidden min-w-fit`}
      >
        {featureSentimentName && (
          <FeatureSentiment feature={featureSentimentName} />
        )}
        {end || ""}
        <EDSThemeToggle
          isDarkMode={colourScheme === "dark"}
          onToggle={handleThemeToggle}
        />
        <div className="ml-2">
          <FullScreen />
        </div>
      </div>
    </div>
  );
};
